import { useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { GrAttachment } from "react-icons/gr";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import {
  ActionIcon,
  Anchor,
  Badge,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useMounted } from "@mantine/hooks";
import { IconSearch, IconStack2, IconX } from "@tabler/icons-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { isValid } from "date-fns";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { CustomPagination } from "@components/CustomPagination";

import { formatDateToString } from "@utils/dateValidation";
import { truncateWithEllipsis } from "@utils/truncateText";

import { getLabsStatuses } from "@data/dropdownValues";

import { labStatusColors } from "@constants/colors";
import { QP } from "@constants/index";

import { useGetClinicLabs } from "@hooks/queries/clinic/useGetClinicLabs";
import { useGetLabsForPatient } from "@hooks/queries/lab/useGetLabsForPatient";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";

import { Laboratory } from "@customTypes/laboratory";

import { TableControls } from "../TableControls";

import { StyledGeneralTabTable } from "./GeneralTab.styled";


const columnHelper = createColumnHelper<Partial<Laboratory>>();

export interface GeneralTabProps {
  setIsRefreshing: (arg: boolean) => void;
}

export const GeneralTab = ({ setIsRefreshing }: GeneralTabProps) => {
  const { t, i18n } = useTranslation();

  const { patientId } = useParams();
  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const [statusFilter, setStatusFilter] = useQueryParam(
    QP.STATUS,
    withDefault(StringParam, "all")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter, statusFilter });

  const [tableData, setTableData] = useState<Partial<Laboratory>[] | []>([]);

  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();

  const { data, isError, isSuccess, isLoading, fetchStatus, isRefetching } =
    useGetLabsForPatient({
      patientId: patientId as string,
      fastConsultationOnly: false,
      pagination: {
        page: tablePagination.page,
        pageSize: tablePagination.pageSize,
      },
      search: debounced.toLocaleLowerCase(),
      status: statusFilter === "all" ? undefined : statusFilter || "",
      category: "bio_analysis",
    });
  const { data: clinicLabsData } = useGetClinicLabs();

  const labColumns = useMemo(
    () => [
      columnHelper.accessor("labType", {
        header: t("lab-type"),
        cell: info => {
          return (
            <p className="type-wrapper">
              {info.row.original.labGroupId ? (
                <IconStack2 size={16} color="accent.4" />
              ) : null}
              {info.row.original.files?.length ? (
                <GrAttachment size={14} />
              ) : null}
              {truncateWithEllipsis(info.cell.getValue(), 20)}
            </p>
          );
        },
      }),
      columnHelper.accessor("consultationInfo.cId", {
        header: t("consultation"),
        cell: info => {
          const consultationDate = info.row.original.consultationInfo?.date;
          return (
            <Group gap={8} align="start">
              <Stack gap={5}>
                <Text fz={14} c="black">
                  {info.cell.getValue() ? (
                    <Anchor
                      fz={14}
                      component={Link}
                      c={"accent.4"}
                      to={`/patients/${patientId}/consultation/general/${info.row.original.consultationId}`}
                      className={"hover-underline"}
                      td="none"
                      ff={"var(--font-secondary)"}
                      fw={450}>
                      {info.cell.getValue()}
                    </Anchor>
                  ) : (
                    "-"
                  )}
                </Text>
                {consultationDate && isValid(consultationDate) && (
                  <Text c="dimmed" fz={13}>
                    {formatDateToString(consultationDate)}
                    {", "}
                    {consultationDate.toLocaleTimeString(i18n.language, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Text>
                )}
              </Stack>
            </Group>
          );
        },
      }),
      columnHelper.accessor("date", {
        header: t("date-created"),
        cell: info => {
          return (
            <Stack gap={5}>
              <Text fz={14}>
                {formatDateToString(info.cell.getValue() as Date)}
              </Text>
              <Text c="dimmed" fz={13}>
                {(info.cell.getValue() as Date).toLocaleTimeString(
                  i18n.language,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.accessor("orderedBy", {
        header: t("ordered-by"),
        cell: info => truncateWithEllipsis(info.cell.getValue()?.fullName, 15),
      }),
      columnHelper.accessor("status", {
        header: t("status"),
        cell: info => {
          return (
            <Badge
              fz={12}
              c={labStatusColors.get(info.cell.getValue() as string)?.c}
              bg={labStatusColors.get(info.cell.getValue() as string)?.bg}>
              {t(`${info.cell.getValue()}`)}
            </Badge>
          );
        },
      }),
      columnHelper.display({
        id: "action",
        header: isMobile ? t("action") : t("last-updated-action"),
        cell: info => {
          return (
            <TableControls
              lab={info.row.original}
              labTypes={clinicLabsData || []}
            />
          );
        },
      }),
    ],
    [tableData, clinicLabsData, i18n.language]
  );

  const table = useReactTable({
    data: tableData,
    columns: labColumns,
    // state: {
    //   globalFilter: searchFilter,
    // },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // onGlobalFilterChange: setSearchFilter,
    // globalFilterFn: (
    //   row: Row<Partial<Laboratory>>,
    //   columnId: string,
    //   value: string
    // ) => {
    //   if (columnId === "labType") {
    //     return filterFns.includesString(row, columnId, value, () => null);
    //   }
    //   return false;
    // },
    debugTable: false,
  });

  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination({ ...data.pagination });
    } else {
      setTableData([]);
    }
  }, [data]);

  // Initial way of syncing table pagination with url
  // useEffect(() => {
  //   // Initial load of data
  //   if (tablePagination.page && !debounced && filterValue === "all") {
  //     refetch();
  //   }

  //   // Filters are updated and page is changed
  //   // Can be combined with condition above, but for readability it will be separated
  //   if (
  //     (debounced || filterValue !== "all") &&
  //     tablePagination.page !== previousPaginationPage
  //   ) {
  //     refetch();
  //   }
  //   // Filter is updated, reset pagination
  //   else if (debounced || filterValue !== "all") {
  //     // console.log("in debounced");
  //     setTablePagination(prev => ({ ...prev, page: 1 }));
  //     refetch();
  //   }
  // }, [debounced, filterValue, tablePagination.page]);

  useEffect(() => {
    if (setIsRefreshing) setIsRefreshing(isRefetching);
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <>
      <LoadingOverlay
        visible={isRefetching && !!data?.newData?.length && !isMounted}
        zIndex={1000}
        overlayProps={{ blur: 0.1, opacity: 0.6 }}
        loaderProps={{ display: "none" }}
      />
      <div className="filters-wrapper">
        <TextInput
          placeholder={t("search-for-record")}
          w={{ base: 300, md: 350, lg: 400 }}
          value={searchFilter}
          onChange={e =>
            setSearchFilter(
              e.currentTarget.value.toLocaleLowerCase(),
              "replaceIn"
            )
          }
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchFilter && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setSearchFilter(null, "replaceIn")}>
                <IconX size={16} />
              </ActionIcon>
            )
          }
        />

        <div className="dropdown-wrapper">
          <p>{t("filter")}: </p>
          <Select
            w={150}
            data={[{ label: t("all"), value: "all" }, ...getLabsStatuses()]}
            value={statusFilter}
            onChange={val => {
              setStatusFilter(val, "replaceIn");
            }}
            disabled={isLoading || isRefetching}
          />
        </div>
      </div>
      <StyledGeneralTabTable>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {isSuccess && !data?.newData?.length && debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={labColumns.length}>
                <p>
                  {t("empty-results-for", {
                    searchFilter: `"${debounced}"`,
                  })}
                </p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isSuccess && !data?.newData?.length && !debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={labColumns.length}>
                <p>{t("empty-labs")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isLoading && fetchStatus === "fetching" && !tableData.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={labColumns.length}>
                <p>{t("loading")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isError && !data?.newData?.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={labColumns.length}>
                <p>{t("encountered-error")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledGeneralTabTable>

      {(isLoading && fetchStatus === "fetching") || !data ? null : (
        <CustomPagination
          table={table}
          tablePagination={tablePagination}
          otherParams={{
            ...(debounced && { [QP.SEARCH]: debounced }),
            [QP.TAB]: "general",
            [QP.STATUS]: statusFilter,
          }}
        />
      )}
    </>
  );
};
